import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Search,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Plus,
  Eye,
  Trash2,
  Shield,
  Ban,
  ChevronLeft,
  ChevronRight,
  Loader,
  X,
  Flag,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const API_BASE_URL =
  process.env.REACT_APP_BASE_URL || "http://localhost:3000/api/v1";

const ReportsManagement = () => {
  const [reports, setReports] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(() => {
    const saved = sessionStorage.getItem("reportsPage");
    return saved ? parseInt(saved, 10) : 1;
  });
  const [pagination, setPagination] = useState({});
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("reports");
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbackPagination, setFeedbackPagination] = useState({});
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const { isDarkMode } = useTheme();

  const [filters, setFilters] = useState({
    status: "All",
  });

  const [actionForm, setActionForm] = useState({
    actionType: "",
    actionDetails: "",
    notesForReporter: "",
  });

  const [reasonForm, setReasonForm] = useState({
    reasonKey: "",
    label: "",
    description: "",
    severity: "medium",
    icon: "alert-circle",
    requiresImmediate: false,
  });

  const [moderationOptions, setModerationOptions] = useState({
    moderatorActions: [],
    reportStatuses: [],
  });

  useEffect(() => {
    sessionStorage.setItem("reportsPage", currentPage.toString());
  }, [currentPage]);

  const getAxiosConfig = () => {
    const token = localStorage.getItem("accessToken");
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  };

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: "10",
      });

      if (filters.status !== "All") {
        params.append("status", filters.status.toLowerCase());
      }

      const response = await axios.get(
        `${API_BASE_URL}/report-post/admin/all?${params.toString()}`,
        getAxiosConfig()
      );

      setReports(response.data.data || []);
      setPagination(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching reports:", err);
      setError(err.response?.data?.message || "Failed to fetch reports");
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters]);

  const fetchReasons = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/report`,
        getAxiosConfig()
      );
      setReasons(response.data || []);
    } catch (err) {
      console.error("Error fetching reasons:", err);
    }
  }, []);

  const fetchModerationOptions = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/report/admin/options`,
        getAxiosConfig()
      );
      setModerationOptions(response.data);
    } catch (err) {
      console.error("Error fetching moderation options:", err);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    fetchReasons();
    fetchModerationOptions();
  }, [fetchReasons, fetchModerationOptions]);

  // Add this useEffect to fetch feedbacks
  useEffect(() => {
    if (activeTab === "feedbacks") {
      fetchFeedbacks();
    }
  }, [activeTab, currentPage]);

  // Add this fetch function
  const fetchFeedbacks = useCallback(async () => {
    try {
      setFeedbackLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: "10",
      });

      const response = await axios.get(
        `${API_BASE_URL}/support/feedback/admin/all?${params.toString()}`,
        getAxiosConfig()
      );

      setFeedbacks(response.data.data || []);
      setFeedbackPagination(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching feedbacks:", err);
      setError(err.response?.data?.message || "Failed to fetch feedbacks");
    } finally {
      setFeedbackLoading(false);
    }
  }, [currentPage]);

  const handleUpdateStatus = async (reportId, newStatus) => {
    try {
      setActionLoading(true);
      await axios.patch(
        `${API_BASE_URL}/report-post/admin/${reportId}/status`,
        {
          status: newStatus,
          moderatorNotes: actionForm.notesForReporter,
        },
        getAxiosConfig()
      );
      await fetchReports();
      setSelectedReport(null);
      setShowReportModal(false);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update status");
    } finally {
      setActionLoading(false);
    }
  };
  const handleFeedbackStatusUpdate = async (
    feedbackId,
    newStatus,
    message = ""
  ) => {
    try {
      setActionLoading(true);

      // FIXED: Added 'admin' to the path to match backend route
      await axios.patch(
        `${API_BASE_URL}/support/feedback/admin/${feedbackId}/status`,
        {
          status: newStatus,
          message: message,
        },
        getAxiosConfig()
      );

      await fetchFeedbacks();
      setSelectedReport(null);
      setShowReportModal(false);
      setError(null);
    } catch (err) {
      console.error("Error updating feedback status:", err);
      setError(
        err.response?.data?.message || "Failed to update feedback status"
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Update the feedback modal to use correct API endpoint
  const handleFeedbackView = (feedback) => {
    setSelectedReport({
      ...feedback,
      isFeedback: true, // Flag to differentiate feedback from reports
    });
    setShowReportModal(true);
  };

  const handleTakeAction = async () => {
    if (!actionForm.actionType) {
      setError("Please select an action type");
      return;
    }

    try {
      setActionLoading(true);
      const userId = JSON.parse(localStorage.getItem("user") || "{}")._id;

      await axios.patch(
        `${API_BASE_URL}/report-post/admin/${selectedReport._id}/action`,
        {
          actionType: actionForm.actionType,
          actionDetails: actionForm.actionDetails,
          notesForReporter: actionForm.notesForReporter,
          actionTakenBy: userId,
        },
        getAxiosConfig()
      );

      await fetchReports();
      setShowActionModal(false);
      setActionForm({
        actionType: "",
        actionDetails: "",
        notesForReporter: "",
      });
      setSelectedReport(null);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to take action");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateReason = async () => {
    try {
      setActionLoading(true);
      await axios.post(`${API_BASE_URL}/report`, reasonForm, getAxiosConfig());
      await fetchReasons();
      setShowReasonModal(false);
      setReasonForm({
        reasonKey: "",
        label: "",
        description: "",
        severity: "medium",
        icon: "alert-circle",
        requiresImmediate: false,
      });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create reason");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteReason = async (reasonId) => {
    if (window.confirm("Are you sure you want to delete this reason?")) {
      try {
        await axios.delete(
          `${API_BASE_URL}/report/${reasonId}`,
          getAxiosConfig()
        );
        await fetchReasons();
        setError(null);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to delete reason");
      }
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      low: isDarkMode
        ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
        : "bg-blue-100 text-blue-700 border-blue-300",
      medium: isDarkMode
        ? "bg-orange-500/20 text-orange-400 border-orange-500/30"
        : "bg-orange-100 text-orange-700 border-orange-300",
      high: isDarkMode
        ? "bg-orange-500/20 text-orange-400 border-orange-500/30"
        : "bg-orange-100 text-orange-700 border-orange-300",
      critical: isDarkMode
        ? "bg-red-500/20 text-red-400 border-red-500/30"
        : "bg-red-100 text-red-700 border-red-300",
    };
    return colors[severity] || colors.medium;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <Clock size={16} />;
      case "under_review":
        return <Search size={16} />;
      case "resolved":
        return <CheckCircle size={16} />;
      case "dismissed":
        return <XCircle size={16} />;
      case "action_taken":
        return <Shield size={16} />;
      default:
        return <AlertCircle size={16} />;
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: isDarkMode
        ? "bg-orange-500/20 text-orange-400"
        : "bg-orange-100 text-orange-700",
      under_review: isDarkMode
        ? "bg-blue-500/20 text-blue-400"
        : "bg-blue-100 text-blue-700",
      resolved: isDarkMode
        ? "bg-green-500/20 text-green-400"
        : "bg-green-100 text-green-700",
      dismissed: isDarkMode
        ? "bg-zinc-800 text-gray-400"
        : "bg-gray-200 text-gray-600",
      action_taken: isDarkMode
        ? "bg-purple-500/20 text-purple-400"
        : "bg-purple-100 text-purple-700",
    };
    return statusStyles[status] || "";
  };

  if (loading && reports.length === 0) {
    return (
      <div
        className={`flex flex-col min-h-screen ${
          isDarkMode ? "bg-black" : "bg-gray-100"
        }`}
      >
        <div
          className={`flex-grow flex items-center justify-center ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          <Loader className="animate-spin" size={32} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${
        isDarkMode ? "bg-black text-white" : "bg-gray-100 text-gray-900"
      }`}
    >
      <div className="flex flex-col gap-5 p-5">
        <div className="flex items-center justify-between">
          <h2
            className={`text-2xl font-bold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Report Management
          </h2>
          <button
            onClick={() => setShowReasonModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-400 text-black font-medium rounded-lg hover:bg-orange-500 transition-colors"
          >
            <Plus size={18} />
            Add Report Reason
          </button>
        </div>

        {error && (
          <div
            className={`border rounded-xl p-4 flex items-start gap-3 ${
              isDarkMode
                ? "bg-red-500/10 border-red-500/30 text-red-400"
                : "bg-red-50 border-red-200 text-red-700"
            }`}
          >
            <AlertCircle size={20} className="flex-shrink-0" />
            <div className="flex-1">{error}</div>
            <button onClick={() => setError(null)}>
              <X size={18} />
            </button>
          </div>
        )}

        {/* Tab Navigation */}
        <div
          className={`flex gap-4 border-b ${
            isDarkMode ? "border-zinc-800" : "border-gray-200"
          }`}
        >
          <button
            onClick={() => setActiveTab("reports")}
            className={`px-4 py-3 font-medium border-b-2 transition-colors ${
              activeTab === "reports"
                ? isDarkMode
                  ? "border-orange-400 text-orange-400"
                  : "border-orange-500 text-orange-600"
                : isDarkMode
                ? "border-transparent text-gray-400 hover:text-white"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            Reports
          </button>
          <button
            onClick={() => setActiveTab("reasons")}
            className={`px-4 py-3 font-medium border-b-2 transition-colors ${
              activeTab === "reasons"
                ? isDarkMode
                  ? "border-orange-400 text-orange-400"
                  : "border-orange-500 text-orange-600"
                : isDarkMode
                ? "border-transparent text-gray-400 hover:text-white"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            Report Reasons ({reasons.length})
          </button>
          <button
            onClick={() => {
              setActiveTab("feedbacks");
              setCurrentPage(1);
            }}
            className={`px-4 py-3 font-medium border-b-2 transition-colors ${
              activeTab === "feedbacks"
                ? isDarkMode
                  ? "border-orange-400 text-orange-400"
                  : "border-orange-500 text-orange-600"
                : isDarkMode
                ? "border-transparent text-gray-400 hover:text-white"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            Feedbacks ({feedbacks.length})
          </button>
        </div>

        {/* Reports Tab */}
        {activeTab === "reports" && (
          <>
            {/* Filter Buttons */}
            <div className="flex gap-3">
              {["All", "Pending", "Under Review", "Resolved", "Dismissed"].map(
                (filter) => (
                  <button
                    key={filter}
                    onClick={() => {
                      setFilters({ ...filters, status: filter });
                      setCurrentPage(1);
                    }}
                    className={`px-4 py-2 rounded-lg transition-colors font-medium ${
                      filters.status === filter
                        ? isDarkMode
                          ? "bg-orange-400 text-black"
                          : "bg-orange-500 text-white"
                        : isDarkMode
                        ? "bg-zinc-800 text-gray-300 hover:bg-zinc-700"
                        : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-300"
                    }`}
                  >
                    {filter}
                  </button>
                )
              )}
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
              <div
                className={`${
                  isDarkMode
                    ? "bg-black border-zinc-800"
                    : "bg-white border-gray-200"
                } border rounded-xl p-5 flex items-center gap-4`}
              >
                <div className="w-12 h-12 bg-orange-400 rounded-full flex items-center justify-center">
                  <AlertCircle className="text-black" size={24} />
                </div>
                <div>
                  <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                    Total Reports
                  </p>
                  <p
                    className={`text-2xl font-bold ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {pagination.total || 0}
                  </p>
                </div>
              </div>

              <div
                className={`${
                  isDarkMode
                    ? "bg-black border-zinc-800"
                    : "bg-white border-gray-200"
                } border rounded-xl p-5 flex items-center gap-4`}
              >
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    isDarkMode ? "bg-zinc-800" : "bg-gray-200"
                  }`}
                >
                  <Clock
                    className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                    size={24}
                  />
                </div>
                <div>
                  <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                    Pending Review
                  </p>
                  <p
                    className={`text-2xl font-bold ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {reports.filter((r) => r.status === "pending").length}
                  </p>
                </div>
              </div>

              <div
                className={`${
                  isDarkMode
                    ? "bg-black border-zinc-800"
                    : "bg-white border-gray-200"
                } border rounded-xl p-5 flex items-center gap-4`}
              >
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    isDarkMode ? "bg-zinc-800" : "bg-gray-200"
                  }`}
                >
                  <CheckCircle
                    className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                    size={24}
                  />
                </div>
                <div>
                  <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                    Resolved
                  </p>
                  <p
                    className={`text-2xl font-bold ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {reports.filter((r) => r.status === "resolved").length}
                  </p>
                </div>
              </div>

              <div
                className={`${
                  isDarkMode
                    ? "bg-black border-zinc-800"
                    : "bg-white border-gray-200"
                } border rounded-xl p-5 flex items-center gap-4`}
              >
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    isDarkMode ? "bg-zinc-800" : "bg-gray-200"
                  }`}
                >
                  <Flag
                    className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                    size={24}
                  />
                </div>
                <div>
                  <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                    Priority Reports
                  </p>
                  <p
                    className={`text-2xl font-bold ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {reports.filter((r) => r.isPriority).length}
                  </p>
                </div>
              </div>
            </div>

            {/* Reports Table */}
            <div
              className={`${
                isDarkMode
                  ? "bg-black border-zinc-800"
                  : "bg-white border-gray-200"
              } border rounded-xl p-5`}
            >
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr
                      className={`border-b ${
                        isDarkMode ? "border-zinc-800" : "border-gray-200"
                      }`}
                    >
                      <th
                        className={`text-left py-3 px-2 text-xs font-medium uppercase tracking-wider ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Reporter
                      </th>
                      <th
                        className={`text-left py-3 px-2 text-xs font-medium uppercase tracking-wider ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Reason
                      </th>
                      <th
                        className={`text-left py-3 px-2 text-xs font-medium uppercase tracking-wider ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Severity
                      </th>
                      <th
                        className={`text-left py-3 px-2 text-xs font-medium uppercase tracking-wider ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Status
                      </th>
                      <th
                        className={`text-left py-3 px-2 text-xs font-medium uppercase tracking-wider ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Date
                      </th>
                      <th
                        className={`text-left py-3 px-2 text-xs font-medium uppercase tracking-wider ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.length === 0 ? (
                      <tr>
                        <td
                          colSpan="6"
                          className={`text-center py-10 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          No reports found
                        </td>
                      </tr>
                    ) : (
                      reports.map((report) => (
                        <tr
                          key={report._id}
                          className={`border-b transition-colors ${
                            isDarkMode
                              ? "border-zinc-800 hover:bg-zinc-900"
                              : "border-gray-200 hover:bg-gray-50"
                          }`}
                        >
                          <td className="py-4 px-2">
                            <div>
                              <p
                                className={`font-medium ${
                                  isDarkMode ? "text-white" : "text-gray-900"
                                }`}
                              >
                                {report.reporterId?.firstName || "User"}
                              </p>
                              <p
                                className={`text-xs ${
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                              >
                                {report.reporterId?.email}
                              </p>
                            </div>
                          </td>
                          <td className="py-4 px-2">
                            <div className="space-y-1">
                              {report.reasonSnapshots
                                ?.slice(0, 2)
                                .map((reason, i) => (
                                  <span
                                    key={i}
                                    className={`inline-block px-2 py-1 rounded text-xs ${getSeverityColor(
                                      reason.severity
                                    )} border`}
                                  >
                                    {reason.label}
                                  </span>
                                ))}
                              {report.reasonSnapshots?.length > 2 && (
                                <p
                                  className={`text-xs ${
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-600"
                                  }`}
                                >
                                  +{report.reasonSnapshots.length - 2} more
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="py-4 px-2">
                            <span
                              className={`inline-block px-2 py-1 rounded text-xs border font-medium ${
                                report.reasonSnapshots?.[0]
                                  ? getSeverityColor(
                                      report.reasonSnapshots[0].severity
                                    )
                                  : getSeverityColor("medium")
                              }`}
                            >
                              {report.reasonSnapshots?.[0]?.severity ||
                                "medium"}
                            </span>
                          </td>
                          <td className="py-4 px-2">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(
                                report.status
                              )}`}
                            >
                              {report.status.replace(/_/g, " ")}
                            </span>
                          </td>
                          <td
                            className={`py-4 px-2 ${
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            {new Date(report.createdAt).toLocaleDateString()}
                          </td>
                          <td className="py-4 px-2">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  setSelectedReport(report);
                                  setShowReportModal(true);
                                }}
                                className={`p-2 rounded transition-colors ${
                                  isDarkMode
                                    ? "hover:bg-zinc-800"
                                    : "hover:bg-gray-100"
                                }`}
                                title="View details"
                              >
                                <Eye size={16} />
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedReport(report);
                                  setShowActionModal(true);
                                }}
                                className={`p-2 rounded transition-colors ${
                                  isDarkMode
                                    ? "hover:bg-zinc-800"
                                    : "hover:bg-gray-100"
                                }`}
                                title="Take action"
                              >
                                <Shield size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div
                className={`flex items-center justify-center gap-4 mt-5 pt-5 border-t ${
                  isDarkMode ? "border-zinc-800" : "border-gray-200"
                }`}
              >
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDarkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <ChevronLeft size={20} />
                </button>
                <span
                  className={`text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Page {pagination.page || 1} of {pagination.totalPages || 1}
                </span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  className={`transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDarkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </>
        )}

        {/* Reasons Tab */}
        {activeTab === "reasons" && (
          <div className="space-y-4">
            {reasons.length === 0 ? (
              <div
                className={`${
                  isDarkMode
                    ? "bg-black border-zinc-800"
                    : "bg-white border-gray-200"
                } border rounded-xl p-12 text-center`}
              >
                <AlertCircle
                  size={32}
                  className={`mx-auto mb-3 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                />
                <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                  No report reasons found
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {reasons.map((reason) => (
                  <div
                    key={reason._id}
                    className={`${
                      isDarkMode
                        ? "bg-black border-zinc-800"
                        : "bg-white border-gray-200"
                    } border rounded-xl p-5 transition-all hover:shadow-lg`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h3
                        className={`font-semibold text-lg ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {reason.label}
                      </h3>
                      <button
                        onClick={() => handleDeleteReason(reason._id)}
                        className={`p-1 rounded transition-colors ${
                          isDarkMode
                            ? "hover:bg-zinc-800 text-red-400 hover:text-red-300"
                            : "hover:bg-red-50 text-red-500 hover:text-red-600"
                        }`}
                        title="Delete reason"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <p
                      className={`text-xs ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      } mb-2 font-mono`}
                    >
                      Key: {reason.reasonKey}
                    </p>

                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      } mb-3`}
                    >
                      {reason.description}
                    </p>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`inline-block px-2 py-1 rounded text-xs border font-medium ${getSeverityColor(
                            reason.severity
                          )}`}
                        >
                          {reason.severity}
                        </span>
                        {reason.requiresImmediate && (
                          <span
                            className={`inline-block px-2 py-1 rounded text-xs border font-medium ${
                              isDarkMode
                                ? "bg-red-500/20 text-red-400 border-red-500/30"
                                : "bg-red-100 text-red-700 border-red-300"
                            }`}
                          >
                            ⚠️ Immediate
                          </span>
                        )}
                      </div>

                      <p
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Usage:{" "}
                        <span className="font-semibold">
                          {reason.usageCount || 0}
                        </span>{" "}
                        times
                      </p>

                      {!reason.isActive && (
                        <div
                          className={`text-xs px-2 py-1 rounded border ${
                            isDarkMode
                              ? "bg-gray-500/20 text-gray-400 border-gray-500/30"
                              : "bg-gray-100 text-gray-700 border-gray-300"
                          }`}
                        >
                          Inactive
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Report Details Modal */}
      {showReportModal && selectedReport && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div
            className={`${
              isDarkMode
                ? "bg-black border-zinc-800"
                : "bg-white border-gray-200"
            } border rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}
          >
            <div
              className={`p-6 border-b ${
                isDarkMode ? "border-zinc-800" : "border-gray-200"
              } flex items-center justify-between sticky top-0 ${
                isDarkMode ? "bg-black" : "bg-white"
              }`}
            >
              <h2
                className={`text-xl font-bold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {selectedReport.isFeedback
                  ? "Feedback Details"
                  : "Report Details"}
              </h2>
              <button
                onClick={() => {
                  setShowReportModal(false);
                  setSelectedReport(null);
                }}
                className={`${
                  isDarkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                } transition-colors`}
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {selectedReport.isFeedback ? (
                <>
                  <div>
                    <h3
                      className={`font-semibold ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      } mb-3`}
                    >
                      Email
                    </h3>
                    <p
                      className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                    >
                      {selectedReport.email}
                    </p>
                  </div>

                  <div>
                    <h3
                      className={`font-semibold ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      } mb-3`}
                    >
                      Subject
                    </h3>
                    <p
                      className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                    >
                      {selectedReport.title}
                    </p>
                  </div>

                  <div>
                    <h3
                      className={`font-semibold ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      } mb-3`}
                    >
                      Description
                    </h3>
                    <p
                      className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                    >
                      {selectedReport.description}
                    </p>
                  </div>

                  <div>
                    <h3
                      className={`font-semibold ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      } mb-3`}
                    >
                      Status
                    </h3>
                    <div className="flex items-center gap-3 flex-wrap">
                      {["open", "in_progress", "resolved"].map((s) => (
                        <button
                          key={s}
                          onClick={() =>
                            handleFeedbackStatusUpdate(selectedReport._id, s)
                          }
                          disabled={actionLoading}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            selectedReport.status === s
                              ? "bg-orange-400 text-black"
                              : isDarkMode
                              ? "bg-zinc-800 text-gray-300 hover:bg-zinc-700"
                              : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                          } disabled:opacity-50`}
                        >
                          {s.replace(/_/g, " ")}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Original report details */}
                  <div>
                    <h3
                      className={`font-semibold ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      } mb-3`}
                    >
                      Reporter
                    </h3>
                    <p
                      className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                    >
                      {selectedReport.reporterId?.firstName}{" "}
                      {selectedReport.reporterId?.lastName}
                    </p>
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      {selectedReport.reporterId?.email}
                    </p>
                  </div>

                  <div>
                    <h3
                      className={`font-semibold ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      } mb-3`}
                    >
                      Description
                    </h3>
                    <p
                      className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                    >
                      {selectedReport.description}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Take Action Modal */}
      {showActionModal && selectedReport && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div
            className={`${
              isDarkMode
                ? "bg-black border-zinc-800"
                : "bg-white border-gray-200"
            } border rounded-xl max-w-md w-full`}
          >
            <div
              className={`p-6 border-b ${
                isDarkMode ? "border-zinc-800" : "border-gray-200"
              } flex items-center justify-between`}
            >
              <h2
                className={`text-xl font-bold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Take Moderator Action
              </h2>
              <button
                onClick={() => {
                  setShowActionModal(false);
                  setActionForm({
                    actionType: "",
                    actionDetails: "",
                    notesForReporter: "",
                  });
                }}
                className={`${
                  isDarkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                } transition-colors`}
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label
                  className={`block text-sm font-medium ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  } mb-2`}
                >
                  Action Type *
                </label>
                <select
                  value={actionForm.actionType}
                  onChange={(e) =>
                    setActionForm({ ...actionForm, actionType: e.target.value })
                  }
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDarkMode
                      ? "bg-zinc-800 border-zinc-700 text-white focus:border-orange-400"
                      : "bg-gray-100 border-gray-300 text-gray-900 focus:border-orange-500"
                  } focus:outline-none focus:ring-2 focus:ring-orange-500/50`}
                >
                  <option value="">Select an action...</option>
                  {moderationOptions.moderatorActions?.map((action) => (
                    <option key={action.key} value={action.key}>
                      {action.label} - {action.description}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  } mb-2`}
                >
                  Action Details
                </label>
                <textarea
                  value={actionForm.actionDetails}
                  onChange={(e) =>
                    setActionForm({
                      ...actionForm,
                      actionDetails: e.target.value,
                    })
                  }
                  placeholder="Provide details about this action..."
                  rows="3"
                  maxLength={1000}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDarkMode
                      ? "bg-zinc-800 border-zinc-700 text-white focus:border-orange-400"
                      : "bg-gray-100 border-gray-300 text-gray-900 focus:border-orange-500"
                  } focus:outline-none focus:ring-2 focus:ring-orange-500/50 resize-none`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  } mb-2`}
                >
                  Notes for Reporter
                </label>
                <textarea
                  value={actionForm.notesForReporter}
                  onChange={(e) =>
                    setActionForm({
                      ...actionForm,
                      notesForReporter: e.target.value,
                    })
                  }
                  placeholder="Message to send to the reporter..."
                  rows="3"
                  maxLength={500}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDarkMode
                      ? "bg-zinc-800 border-zinc-700 text-white focus:border-orange-400"
                      : "bg-gray-100 border-gray-300 text-gray-900 focus:border-orange-500"
                  } focus:outline-none focus:ring-2 focus:ring-orange-500/50 resize-none`}
                />
              </div>
            </div>

            <div
              className={`p-6 border-t ${
                isDarkMode ? "border-zinc-800" : "border-gray-200"
              } flex gap-3 justify-end`}
            >
              <button
                onClick={() => {
                  setShowActionModal(false);
                  setActionForm({
                    actionType: "",
                    actionDetails: "",
                    notesForReporter: "",
                  });
                }}
                className={`px-4 py-2 rounded-lg transition-colors font-medium ${
                  isDarkMode
                    ? "bg-zinc-800 text-gray-300 hover:bg-zinc-700"
                    : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleTakeAction}
                disabled={actionLoading || !actionForm.actionType}
                className={`px-4 py-2 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDarkMode
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-red-500 hover:bg-red-600"
                }`}
              >
                {actionLoading ? "Processing..." : "Take Action"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Report Reason Modal */}
      {showReasonModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div
            className={`${
              isDarkMode
                ? "bg-black border-zinc-800"
                : "bg-white border-gray-200"
            } border rounded-xl max-w-md w-full`}
          >
            <div
              className={`p-6 border-b ${
                isDarkMode ? "border-zinc-800" : "border-gray-200"
              } flex items-center justify-between`}
            >
              <h2
                className={`text-xl font-bold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Create Report Reason
              </h2>
              <button
                onClick={() => {
                  setShowReasonModal(false);
                  setReasonForm({
                    reasonKey: "",
                    label: "",
                    description: "",
                    severity: "medium",
                    icon: "alert-circle",
                    requiresImmediate: false,
                  });
                }}
                className={`${
                  isDarkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                } transition-colors`}
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label
                  className={`block text-sm font-medium ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  } mb-2`}
                >
                  Reason Key *
                </label>
                <input
                  type="text"
                  value={reasonForm.reasonKey}
                  onChange={(e) =>
                    setReasonForm({ ...reasonForm, reasonKey: e.target.value })
                  }
                  placeholder="e.g., spam, harassment"
                  maxLength={50}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDarkMode
                      ? "bg-zinc-800 border-zinc-700 text-white focus:border-orange-400"
                      : "bg-gray-100 border-gray-300 text-gray-900 focus:border-orange-500"
                  } focus:outline-none focus:ring-2 focus:ring-orange-500/50`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  } mb-2`}
                >
                  Label *
                </label>
                <input
                  type="text"
                  value={reasonForm.label}
                  onChange={(e) =>
                    setReasonForm({ ...reasonForm, label: e.target.value })
                  }
                  placeholder="e.g., Spam Content"
                  maxLength={100}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDarkMode
                      ? "bg-zinc-800 border-zinc-700 text-white focus:border-orange-400"
                      : "bg-gray-100 border-gray-300 text-gray-900 focus:border-orange-500"
                  } focus:outline-none focus:ring-2 focus:ring-orange-500/50`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  } mb-2`}
                >
                  Description *
                </label>
                <textarea
                  value={reasonForm.description}
                  onChange={(e) =>
                    setReasonForm({
                      ...reasonForm,
                      description: e.target.value,
                    })
                  }
                  placeholder="Describe this report reason..."
                  maxLength={500}
                  rows="3"
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDarkMode
                      ? "bg-zinc-800 border-zinc-700 text-white focus:border-orange-400"
                      : "bg-gray-100 border-gray-300 text-gray-900 focus:border-orange-500"
                  } focus:outline-none focus:ring-2 focus:ring-orange-500/50 resize-none`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  } mb-2`}
                >
                  Severity *
                </label>
                <select
                  value={reasonForm.severity}
                  onChange={(e) =>
                    setReasonForm({ ...reasonForm, severity: e.target.value })
                  }
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDarkMode
                      ? "bg-zinc-800 border-zinc-700 text-white focus:border-orange-400"
                      : "bg-gray-100 border-gray-300 text-gray-900 focus:border-orange-500"
                  } focus:outline-none focus:ring-2 focus:ring-orange-500/50`}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  } mb-2`}
                >
                  Icon Name
                </label>
                <input
                  type="text"
                  value={reasonForm.icon}
                  onChange={(e) =>
                    setReasonForm({ ...reasonForm, icon: e.target.value })
                  }
                  placeholder="e.g., alert-circle, shield-alert"
                  maxLength={50}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDarkMode
                      ? "bg-zinc-800 border-zinc-700 text-white focus:border-orange-400"
                      : "bg-gray-100 border-gray-300 text-gray-900 focus:border-orange-500"
                  } focus:outline-none focus:ring-2 focus:ring-orange-500/50`}
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="requiresImmediate"
                  checked={reasonForm.requiresImmediate}
                  onChange={(e) =>
                    setReasonForm({
                      ...reasonForm,
                      requiresImmediate: e.target.checked,
                    })
                  }
                  className="w-4 h-4"
                />
                <label
                  htmlFor="requiresImmediate"
                  className={`text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Requires Immediate Attention
                </label>
              </div>
            </div>

            <div
              className={`p-6 border-t ${
                isDarkMode ? "border-zinc-800" : "border-gray-200"
              } flex gap-3 justify-end`}
            >
              <button
                onClick={() => {
                  setShowReasonModal(false);
                  setReasonForm({
                    reasonKey: "",
                    label: "",
                    description: "",
                    severity: "medium",
                    icon: "alert-circle",
                    requiresImmediate: false,
                  });
                }}
                className={`px-4 py-2 rounded-lg transition-colors font-medium ${
                  isDarkMode
                    ? "bg-zinc-800 text-gray-300 hover:bg-zinc-700"
                    : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateReason}
                disabled={
                  actionLoading ||
                  !reasonForm.reasonKey ||
                  !reasonForm.label ||
                  !reasonForm.description
                }
                className="px-4 py-2 bg-orange-400 text-black font-medium rounded-lg hover:bg-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? "Creating..." : "Create Reason"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Feedbacks Tab */}
      {activeTab === "feedbacks" && (
        <>
          {feedbackLoading && feedbacks.length === 0 ? (
            <div className="flex items-center justify-center py-10">
              <Loader className="animate-spin" size={32} />
            </div>
          ) : (
            <>
              <div
                className={`${
                  isDarkMode
                    ? "bg-black border-zinc-800"
                    : "bg-white border-gray-200"
                } border rounded-xl p-5`}
              >
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr
                        className={`border-b ${
                          isDarkMode ? "border-zinc-800" : "border-gray-200"
                        }`}
                      >
                        <th
                          className={`text-left py-3 px-2 text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Email
                        </th>
                        <th
                          className={`text-left py-3 px-2 text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Subject
                        </th>
                        <th
                          className={`text-left py-3 px-2 text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Status
                        </th>
                        <th
                          className={`text-left py-3 px-2 text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Date
                        </th>
                        <th
                          className={`text-left py-3 px-2 text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {feedbacks.length === 0 ? (
                        <tr>
                          <td
                            colSpan="5"
                            className={`text-center py-10 ${
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            No feedbacks found
                          </td>
                        </tr>
                      ) : (
                        feedbacks.map((feedback) => (
                          <tr
                            key={feedback._id}
                            className={`border-b transition-colors ${
                              isDarkMode
                                ? "border-zinc-800 hover:bg-zinc-900"
                                : "border-gray-200 hover:bg-gray-50"
                            }`}
                          >
                            <td className="py-4 px-2">
                              <p
                                className={`text-sm ${
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                              >
                                {feedback.email}
                              </p>
                            </td>
                            <td className="py-4 px-2">
                              <p
                                className={`${
                                  isDarkMode ? "text-white" : "text-gray-900"
                                }`}
                              >
                                {feedback.title?.substring(0, 50)}...
                              </p>
                            </td>
                            <td className="py-4 px-2">
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(
                                  feedback.status
                                )}`}
                              >
                                {feedback.status?.replace(/_/g, " ") || "open"}
                              </span>
                            </td>
                            <td
                              className={`py-4 px-2 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {new Date(
                                feedback.createdAt
                              ).toLocaleDateString()}
                            </td>
                            <td className="py-4 px-2">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleFeedbackView(feedback)}
                                  className={`p-2 rounded transition-colors ${
                                    isDarkMode
                                      ? "hover:bg-zinc-800"
                                      : "hover:bg-gray-100"
                                  }`}
                                  title="View details"
                                >
                                  <Eye size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div
                  className={`flex items-center justify-center gap-4 mt-5 pt-5 border-t ${
                    isDarkMode ? "border-zinc-800" : "border-gray-200"
                  }`}
                >
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      isDarkMode
                        ? "text-gray-400 hover:text-white"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <span
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Page {feedbackPagination.page || 1} of{" "}
                    {feedbackPagination.totalPages || 1}
                  </span>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!feedbackPagination.hasNextPage}
                    className={`transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      isDarkMode
                        ? "text-gray-400 hover:text-white"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ReportsManagement;
